import { message } from '@/utils/toast'
import { clearRecentApiErrorMessage } from '@/utils/apiErrorDisplayState'

/**
 * 统一的 API 错误类
 * 用于区分系统级错误和业务级错误
 */
export class ApiError extends Error {
  public readonly code: number
  public readonly detail?: unknown
  public readonly handledByInterceptor: boolean

  constructor(code: number, message: string, detail?: unknown, options: { handledByInterceptor?: boolean } = {}) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.detail = detail
    this.handledByInterceptor = options.handledByInterceptor ?? false
  }

  /** 是否为认证错误 */
  get isAuthError(): boolean {
    return this.code === 401
  }

  /** 是否为权限错误 */
  get isPermissionError(): boolean {
    return this.code === 403
  }

  /** 是否为业务逻辑错误 */
  get isBusinessError(): boolean {
    return this.code >= 1000
  }
}

export function resolveApiErrorMessage(payload: unknown, fallbackMessage = '请求失败'): string {
  if (payload && typeof payload === 'object') {
    const data = payload as Record<string, unknown>
    const candidates = [data.msg, data.message, data.error]

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        const fieldErrors = extractFieldErrors(data.data)
        return fieldErrors ? `${candidate.trim()}：${fieldErrors}` : candidate.trim()
      }
    }
  }

  return fallbackMessage
}

export function resolveCaughtApiErrorMessage(error: unknown, fallbackMessage = '请求失败'): string {
  const responseMessage = resolveApiErrorMessage((error as any)?.response?.data, '')
  if (responseMessage) return responseMessage

  if (error && typeof error === 'object' && !(error instanceof Error)) {
    const directMessage = resolveApiErrorMessage(error, '')
    if (directMessage) return directMessage
  }

  const dataMessage = resolveApiErrorMessage((error as any)?.data, '')
  if (dataMessage) return dataMessage

  if (error instanceof ApiError && error.message.trim()) {
    return error.message.trim()
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }

  return fallbackMessage
}

export function showApiError(error: unknown, fallbackMessage = '请求失败'): string {
  if (
    error instanceof ApiError &&
    (error.handledByInterceptor || error.isAuthError || error.isPermissionError)
  ) {
    return error.message || fallbackMessage
  }

  const errorMessage = resolveCaughtApiErrorMessage(error, fallbackMessage)
  clearRecentApiErrorMessage()
  message.error(errorMessage)
  return errorMessage
}

function extractFieldErrors(data: unknown): string | null {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const msgs = Object.values(data as Record<string, unknown>).filter(
      (v): v is string => typeof v === 'string' && v.trim() !== ''
    )
    return msgs.length > 0 ? msgs.join('；') : null
  }
  return null
}
